import { Suspense, useState, Fragment, type ComponentType, type ReactElement, type ReactNode } from 'react'
import { useMutation, useSuspenseQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import { Link, useNavigate } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useLingui } from '@lingui/react/macro'
import {
  LogOutIcon,
  MoonIcon,
  SunIcon,
  RefreshCwIcon,
  SettingsIcon,
  UserIcon,
  LanguagesIcon,
  ShieldIcon,
  FlaskConicalIcon,
  Undo2Icon,
  ChevronRightIcon,
} from 'lucide-react'
import { Drawer, DrawerContent, DrawerItem, DrawerSeparator, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks/use-mobile'
import { Avatar } from './shared/avatar'
import type { ProfileType } from 'shared'
import { clearQueryClient, trpc } from '@/main'
import { beginLogout } from '@/lib/logout-signal'
import { useActiveEmployer } from '@/hooks/use-active-employer'
import { usePermissions } from '@/hooks/use-permissions'
import { useAppStore } from '@/lib/app-store'
import { ProfileVisibilityDialog } from './user-menu-components/profile-visibility-dialog'
import { ChangeLanguageDialog } from './user-menu-components/change-language-dialog'
import { DevLoginAs } from './user-menu-components/dev-login-as'

// DEV ESCAPE HATCH: eligible sessions get a creatable select that logs in as any user by email (see
// DevLoginAs) — the strictly-more-powerful replacement for the old "Switch to account" feature, which
// has been removed. Driven entirely by the server's `user.canDevLoginAs` (true only for a super admin
// who signed in via Google — see setDevSuperAdminCookie), in any environment, so the UI matches the
// backend guard exactly.

type EmployeeProfile = inferRouterOutputs<AppRouter>['employees']['getMyEmployeeProfile']

// The menu renders into two surfaces — a dropdown on a pointer-sized screen, a bottom SHEET below `md`, where
// the trigger avatar sits in the far corner from the thumb and a dozen small targets hang off it. Only the row
// PRIMITIVES differ; the body below is written once, so the two can't drift apart as items are added. The prop
// shape is the dropdown item's, which the sheet row deliberately mirrors (see DrawerItem).
interface MenuRowProps {
  children?: ReactNode
  className?: string
  disabled?: boolean
  /** Keep the surface open while the row's async work runs, so its spinner stays on screen. */
  closeOnClick?: boolean
  onClick?: () => void
  render?: ReactElement
}

interface MenuSurface {
  Item: ComponentType<MenuRowProps>
  Separator: ComponentType
}

const DROPDOWN_SURFACE: MenuSurface = { Item: DropdownMenuItem, Separator: DropdownMenuSeparator }
const SHEET_SURFACE: MenuSurface = { Item: DrawerItem, Separator: DrawerSeparator }

export function UserMenu() {
  // getMyEmployeeProfile resolves the active employer from the session and returns null in the personal
  // D'Anchiano context (or for anonymous), so there's no need to branch on isEmployee/activeEmployerId
  // here — the menu reads the active-employer copy when there is one, else falls back to the own profile.
  const { data: employeeProfile } = useSuspenseQuery(trpc.employees.getMyEmployeeProfile.queryOptions())
  return <UserMenuContent employeeProfile={employeeProfile} />
}

function UserMenuContent(props: { employeeProfile: EmployeeProfile }) {
  const { t } = useLingui()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  // Professional who self-registered (not invited/registered by a company)
  const isSelfApplicant = user?.type === 'applicant' && !user?.isEmployee
  // Professional registered as an employee by a company
  const isEmployee = user?.type === 'applicant' && user?.isEmployee
  // Offer the dev "log in as" select only when the server says this session qualifies (super admin via
  // Google — see backend canDevLoginAs).
  const showDevLoginAs = !!user?.canDevLoginAs
  // Retake is per-employer: gate it on the ACTIVE employer's flag, not the aggregate of all employers.
  const { employers, activeEmployer, activeEmployerId } = useActiveEmployer()
  const { interfaceMode } = usePermissions()
  // Block B (principal admin conmutador) is a distinct single-company IDENTITY switch (company account ↔
  // its admin-self employee row), kept on the legacy toggle below. Everything else here is Block C: a real
  // employee who may be an admin/authorized member of one or more companies under the same email.
  const isBlockB =
    (user?.type === 'company' && user.adminEmployeeId != null) ||
    (user?.type === 'applicant' && user.adminOfCompanyId != null)
  // Acting as a company for the active employer (Block C company view) — drives the "acting as" relation
  // header + the "back to my profile" exit. Excludes Block B (its own toggle) and pure company accounts.
  const inCompanyView = isEmployee && !isBlockB && interfaceMode === 'company'
  // Unified profile switcher (same from every perspective — personal, an employee context, or a company
  // view): ONE list of all your profiles, so you can rotate to any of them from anywhere. It lists your
  // personal D'Anchiano profile + every company you're an employee of; each company row enters its employee
  // profile, with a "see as company" action for admin/authorized members. The CURRENTLY selected option is
  // omitted to avoid clutter (you're already there). Shown for ANY employee — INCLUDING a principal admin
  // (Block B), who may also be an employee of OTHER companies under the same email.
  const showSwitcher = isEmployee
  // The employee→company-account conmutador target (Block B). It's listed like every other company (with
  // its name + a "see as employee" row); the difference is only in the "see as company" ACTION — for the
  // principal it switches into the real company/owner account (Block B toggle) rather than the in-session
  // company view (viewAsCompany). See the map below.
  const principalCompanyId = user?.type === 'applicant' ? user.adminOfCompanyId : null
  const switcherCompanies = employers.filter((e) => e.companyId != null)
  const activeEmployerName = activeEmployer?.name ?? ''
  // In an employer context we show the employee's data exactly as the company stores it — no
  // fallback to the person's own account. The company captures this data when it adds the employee
  // (copied from the applicant), so the employee record is self-contained.
  const menuName = props.employeeProfile
    ? `${props.employeeProfile.firstname ?? ''} ${props.employeeProfile.lastname ?? ''}`.trim()
    : user?.name
  const menuEmail = props.employeeProfile ? props.employeeProfile.email : user?.email
  const menuImage = props.employeeProfile ? props.employeeProfile.image : user?.image
  const navigate = useNavigate()
  const darkMode = useAppStore((state) => state.store.darkMode)
  const setStore = useAppStore((state) => state.setStore)
  // Same breakpoint the section nav moves to the bottom bar at: below it, chrome belongs in the thumb's reach.
  const isMobile = useIsMobile()
  const { Item, Separator } = isMobile ? SHEET_SURFACE : DROPDOWN_SURFACE
  // A row that opens a DIALOG behaves oppositely on the two surfaces, and both are load-bearing:
  //   • dropdown — let the menu close first (the race described below).
  //   • sheet — keep it OPEN and let the dialog stack on top. Dismissing it instead ran both travels at once
  //     (the dialog reached full opacity ~170ms before the sheet had finished sliding away, with the two
  //     backdrops overlapping for most of it), and left nothing behind the dialog to come back to.
  const dialogRow: { closeOnClick?: boolean } = isMobile ? { closeOnClick: false } : {}

  const [visible, setVisible] = useState(false)
  // Dialogs opened from menu items are rendered OUTSIDE the dropdown (below) and
  // controlled by these flags. Opening them via a DialogTrigger inside a menu item
  // races the menu's modal/dismiss layer; letting the menu close and opening the
  // dialog from the item's `onClick` (base-ui's activation callback) is the reliable pattern.
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false)
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false)
  const { mutateAsync: logout, isPending: isLoggingOut } = useMutation(
    trpc.auth.logout.mutationOptions({
      // Mark logout in progress before the cache clears, so the topbar keeps a neutral
      // skeleton instead of flashing the anonymous "compare my profile" CTA in the gap
      // before the hard redirect below.
      onMutate: () => {
        beginLogout()
      },
      onSettled: () => {
        // Logout is now always a full logout → anonymous (the switchInterface origin chain dies with the
        // session). Returning to a prior identity is a separate "go back" action (returnInterface below),
        // not a side effect of logout. Drop everything and client-nav to the public, auth-less /login.
        clearQueryClient()
        navigate({ to: '/login', replace: true })
      },
    }),
  )

  // Return to the previous identity after a switch/impersonation (replaces the old altUser-on-logout
  // path). Full reload, like every identity transition — switching account context leaves stale query +
  // mounted-component state that only a clean document load fully resets. `search: {}` drops the
  // switched-into account's report/profile params so the restored identity never lands on a stale resource.
  const { mutateAsync: returnInterface, isPending: isReturning } = useMutation(
    trpc.auth.switchInterface.mutationOptions({
      onSuccess: (restored) => {
        navigate({ to: restored.type === 'company' ? '/employees' : '/report', search: {}, reloadDocument: true })
      },
    }),
  )

  // Conmutador (Block B only): the principal admin's IDENTITY switch between the company account and its
  // admin-self employee row (adminEmployeeId ↔ adminOfCompanyId). Block C's company view is driven by the
  // per-company "View as company" buttons below, not this toggle. Always a full reload (handled below).
  const interfaceToggle =
    // Company side: only offer "View as employee" once the admin-self has CONFIRMED registration (set a
    // password via the invite email). Before that the identity can't log in / has no report to switch to.
    // If registered but the test isn't done yet, switching lands them on the applicant test-gate, which
    // resumes the questionnaire automatically (route-access).
    user?.type === 'company' && user.adminEmployeeId != null && user.adminSelfRegistered
      ? { label: t`View as employee`, to: '/report' as const }
      : user?.type === 'applicant' && user.adminOfCompanyId != null
        ? { label: t`View as company`, to: '/employees' as const }
        : null
  const { mutateAsync: toggleInterface, isPending: isToggling } = useMutation(
    trpc.auth.switchInterface.mutationOptions({
      onSuccess: () => {
        navigate({ to: interfaceToggle?.to ?? '/report', search: {}, reloadDocument: true })
      },
    }),
  )

  // Block C — enter a specific company's admin view. `switchInterface({kind:'viewAsCompany'})` sets the
  // active employer AND the company interface mode in one step (server-gated on that company's role), so it
  // works even for a company that isn't the currently-active employer. `variables` tells us WHICH button is
  // in flight so only that row shows a spinner. Full reload, like every context transition.
  const {
    mutateAsync: viewAsCompany,
    isPending: isEnteringCompany,
    variables: enteringVars,
  } = useMutation(
    trpc.auth.switchInterface.mutationOptions({
      onSuccess: () => navigate({ to: '/employees', search: {}, reloadDocument: true }),
    }),
  )

  // Block C — set the active EMPLOYEE context: `null` returns to the personal D'Anchiano profile (also
  // dropping any company interface mode server-side), a `companyId` enters that company's employee profile.
  // `variables` (the target id) tells us which row is in flight so only it shows a spinner. Full reload.
  const {
    mutateAsync: switchEmployer,
    isPending: isSwitchingEmployer,
    variables: switchVars,
  } = useMutation(
    trpc.employees.setActiveEmployer.mutationOptions({
      onSuccess: () => navigate({ to: '/report', search: {}, reloadDocument: true }),
    }),
  )

  // The trigger avatar mirrors the "acting as" relation while in the company view (company primary, your
  // own avatar as an overlay badge), so it's visible from the collapsed menu too — not only once opened.
  const avatarContent =
    inCompanyView && activeEmployer?.companyId != null ? (
      <div className="relative">
        <Avatar size="md" type="company" name={activeEmployer.name} url={activeEmployer.image} />
        <Avatar
          size="xs"
          type="applicant"
          name={menuName}
          url={menuImage}
          className="ring-background absolute -right-1 -bottom-1 ring-2"
        />
      </div>
    ) : (
      <div className="flex items-center gap-2">
        <div className="relative">
          <Avatar size="md" url={menuImage} type={user?.type} name={menuName} />
        </div>
      </div>
    )

  // A single profile-switcher action row: an avatar + the full action label + a chevron, styled as a
  // `bg-muted/40` chip. Every action (personal, an employee context, a company view) is its own row — a
  // company that offers both "see as employee" and "see as company" renders TWO rows, not two columns.
  // The label carries the company name, so the whole action reads on one line (wraps if long, never
  // truncates) at a consistent size.
  const renderActionRow = (opts: {
    keyId: string | number
    avatarType: ProfileType | undefined
    avatarName: string | null
    avatarImage: string | null
    label: string
    pending: boolean
    onClick: () => void
  }) => {
    // Any in-flight identity change disables every row (prevents double-switches) — including the
    // principal-admin conmutador toggle, now rendered as one of these chips.
    const busy = isSwitchingEmployer || isEnteringCompany || isToggling
    return (
      <div key={opts.keyId} className="px-2 pb-1.5">
        <Item
          // base-ui Menu.Item fires `onClick`, not Radix's `onSelect` (silently ignored). Keep the menu
          // open during the async switch (spinner stays visible) via base-ui's `closeOnClick={false}`.
          closeOnClick={false}
          onClick={() => {
            if (busy) return
            opts.onClick()
          }}
          disabled={busy}
          className="bg-muted/40 focus:bg-muted gap-2 rounded-md px-2 py-1.5 disabled:opacity-100"
        >
          <Avatar size="sm" type={opts.avatarType} name={opts.avatarName} url={opts.avatarImage} />
          <span className="min-w-0 flex-1 text-xs leading-tight font-medium">{opts.label}</span>
          {opts.pending ? (
            <Spinner className="h-4 w-4 shrink-0 self-center" />
          ) : (
            <ChevronRightIcon className="text-muted-foreground h-4 w-4 shrink-0 self-center" />
          )}
        </Item>
      </div>
    )
  }

  const trigger = (
    <Button variant="ghost" className="h-auto rounded-lg border-0 p-0.5">
      {avatarContent}
    </Button>
  )

  const menuContent = (
    <>
      {/* Profile Section. In the company (admin) view this becomes the "acting as" relation display:
          the COMPANY is the primary avatar (that's the identity you're operating under), with your own
          avatar as a small overlay badge and your name as a caption — so it's always clear it's still
          you behind the company. Otherwise it's the normal identity header, with a shortcut back to the
          personal profile on the right whenever an employer is active. */}
      {inCompanyView && activeEmployer?.companyId != null ? (
        <div className="px-2 py-3">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <Avatar size="lg" type="company" name={activeEmployer.name} url={activeEmployer.image} />
              <Avatar
                size="xs"
                type="applicant"
                name={menuName}
                url={menuImage}
                className="ring-background absolute -right-1 -bottom-1 ring-2"
              />
            </div>
            <div className="flex flex-col gap-0.5 overflow-hidden">
              <p className="truncate text-sm font-semibold">{activeEmployer.name}</p>
              <p className="text-muted-foreground truncate text-xs">{menuName}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-2 py-3">
          <div className="flex items-center gap-3">
            <Avatar size="lg" url={menuImage} type={user?.type} name={menuName} />
            <div className="flex flex-1 flex-col gap-1 overflow-hidden">
              <p className="truncate text-sm font-semibold">{menuName}</p>
              <p className="text-muted-foreground truncate text-xs">{menuEmail}</p>
              {/* In an employee context, make clear WHICH company you're currently acting as an
                  employee of (not just the email). Hidden on the personal D'Anchiano profile. */}
              {isEmployee && activeEmployer?.companyId != null && (
                <span className="text-muted-foreground truncate text-xs">{t`Employee of ${activeEmployerName}`}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unified profile switcher (Block C): the SAME list from every perspective — every company you're
          an employee of + your personal D'Anchiano profile LAST. To avoid clutter the CURRENTLY selected
          option is omitted (you're already there); the alternate action of the current company stays
          (e.g. from a company's employee view you can still "see as company"). Flows straight from the
          header (no section label / leading separator). */}
      {showSwitcher && (
        <>
          {switcherCompanies.map((company) => {
            const companyId = company.companyId as number
            const companyName = company.name ?? ''
            const canView = company.employeeType === 'administrator' || company.employeeType === 'authorized'
            // The principal company: its "see as company" switches into the real company/owner ACCOUNT
            // (Block B cross-login toggle), not the in-session company view — but it's listed exactly
            // like the others (name + both rows) instead of a nameless standalone toggle.
            const isPrincipal = companyId === principalCompanyId
            // Each action is its OWN row (the current context's action is omitted). The "see as employee"
            // row uses YOUR (the employee's) avatar; the "see as company" row uses the company logo.
            const showEmployee = !(!inCompanyView && activeEmployerId === companyId)
            const showCompany = canView && !(inCompanyView && activeEmployerId === companyId)
            return (
              <Fragment key={companyId}>
                {showEmployee &&
                  renderActionRow({
                    keyId: `${companyId}-employee`,
                    avatarType: 'employee',
                    avatarName: user?.name ?? null,
                    avatarImage: company.employeeImage ?? null,
                    label: t`My profile as employee of ${companyName}`,
                    pending: isSwitchingEmployer && switchVars === companyId,
                    onClick: () => void switchEmployer(companyId),
                  })}
                {showCompany &&
                  renderActionRow({
                    keyId: `${companyId}-company`,
                    avatarType: 'company',
                    avatarName: company.name,
                    avatarImage: company.image,
                    label: t`See ${companyName}`,
                    pending: isPrincipal
                      ? isToggling
                      : isEnteringCompany &&
                        enteringVars?.kind === 'viewAsCompany' &&
                        enteringVars.companyId === companyId,
                    // Principal → switch into the actual company/owner account (Block B); others →
                    // in-session company view.
                    onClick: isPrincipal
                      ? () => void toggleInterface({ kind: 'toggle' })
                      : () => void viewAsCompany({ kind: 'viewAsCompany', companyId }),
                  })}
              </Fragment>
            )
          })}
          {/* Personal profile — always LAST; hidden when it's the current context. Only kept for
              accounts first registered directly on D'Anchiano (createdByCompanyId == null): a
              company-minted employee's space is the company's, not a personal professional profile,
              so they don't get this switch. */}
          {activeEmployerId !== null &&
            user?.createdByCompanyId == null &&
            renderActionRow({
              keyId: 'personal',
              avatarType: user?.type,
              avatarName: user?.name ?? null,
              avatarImage: user?.image ?? null,
              label: t`See as personal profile`,
              pending: isSwitchingEmployer && switchVars === null,
              onClick: () => void switchEmployer(null),
            })}
        </>
      )}

      {/* Principal-admin conmutador (Block B), COMPANY-ACCOUNT side only: a chip into its principal
          employee. (The employee→company direction is rendered above as the principal company's own
          row in the switcher list, so it isn't repeated here.) A company account has no Block C list,
          so this is its sole switch. */}
      {user?.type === 'company' &&
        interfaceToggle &&
        renderActionRow({
          keyId: 'principal-toggle',
          avatarType: 'employee',
          avatarName: menuName ?? null,
          avatarImage: null,
          label: interfaceToggle.label,
          pending: isToggling,
          onClick: () => void toggleInterface({ kind: 'toggle' }),
        })}
      <Separator />

      {showDevLoginAs && (
        <>
          <DevLoginAs />
          {/* Super-admin-only dev playground (email templates + component demos), gated on the same
              flag as Dev login-as. */}
          <Item
            render={
              <Link to="/dev-resources" className="gap-3">
                <FlaskConicalIcon className="h-4 w-4" />
                {t`Dev resources`}
              </Link>
            }
          />
          <Separator />
        </>
      )}

      {user?.type === 'applicant' && (
        <>
          {/* Hidden: ProfileViewsDialog. The "not displaying correctly" issue was the
              old menu→dialog composition race; the component is now controlled
              (open/onOpenChange), so re-enable like visibility/language: a menu item
              with onClick={() => setViewsDialogOpen(true)} here, and
              <ProfileViewsDialog viewsCount={user.views} open={viewsDialogOpen}
              onOpenChange={setViewsDialogOpen} /> rendered OUTSIDE the dropdown below. */}
          {/* Profile visibility is a setting on the person's OWN (D'Anchiano) account, so it's
              shown only in the personal context — not while an employer is selected. Going public
              is the person's own choice; companies that added them keep explicit access regardless. */}
          {activeEmployerId === null && (
            <>
              <Item {...dialogRow} onClick={() => setVisibilityDialogOpen(true)} className="flex items-center gap-3">
                <ShieldIcon className="h-4 w-4 shrink-0" />
                <span className="flex-1">{t`Profile visibility`}</span>
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
                    user?.visibility
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                      : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                  )}
                >
                  {user?.visibility ? t`Public` : t`Private`}
                </span>
              </Item>
              <Separator />
            </>
          )}

          {/* Retake test — only when the ACTIVE employer allows it (not just any of the person's employers) */}
          {isEmployee && activeEmployer?.reevaluate && (
            <>
              <Item
                render={
                  <Link to="/test/competences" search={{ retake: true }} className="flex gap-3">
                    <RefreshCwIcon className="h-4 w-4" />
                    {t`Retake test`}
                  </Link>
                }
              />
              <Separator />
            </>
          )}
        </>
      )}

      {/* Settings — "My profile data" for a professional in their OWN profile (self-registered or an
          employee in employee view); "Settings" (company Configuración) for a company account AND an
          admin/authorized employee acting as the company (company view → /account shows company config). */}
      <Item
        render={
          <Link to="/account" className="gap-3">
            {(isSelfApplicant || isEmployee) && !inCompanyView ? (
              <UserIcon className="h-4 w-4" />
            ) : (
              <SettingsIcon className="h-4 w-4" />
            )}
            {(isSelfApplicant || isEmployee) && !inCompanyView ? t`My profile data` : t`Settings`}
          </Link>
        }
      />

      {/* Preferences */}
      <Item
        onClick={() => {
          setStore({
            darkMode:
              darkMode === 'dark' ||
              (darkMode !== 'light' &&
                typeof window !== 'undefined' &&
                window.matchMedia('(prefers-color-scheme: dark)').matches)
                ? 'light'
                : 'dark',
          })
          setVisible(false)
        }}
        className="gap-3"
      >
        {darkMode === 'dark' ||
        (darkMode !== 'light' &&
          typeof window !== 'undefined' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches) ? (
          <>
            <SunIcon className="h-4 w-4" />
            {t`Disable dark mode`}
          </>
        ) : (
          <>
            <MoonIcon className="h-4 w-4" />
            {t`Enable dark mode`}
          </>
        )}
      </Item>

      <Item {...dialogRow} className="flex items-center gap-3" onClick={() => setLanguageDialogOpen(true)}>
        <LanguagesIcon className="h-4 w-4 shrink-0" />
        <span className="flex-1">{t`Change language`}</span>
      </Item>

      <Separator />
      {/* Return to the previous identity after a switch/impersonation (replaces the old
          un-impersonate-via-logout). Only shown when there's a return-chain to pop. */}
      {user?.canReturn && (
        <Item
          // base-ui uses `onClick` (not `onSelect`); keep the menu open for the spinner via closeOnClick.
          closeOnClick={false}
          onClick={() => {
            if (isReturning) return
            void returnInterface({ kind: 'return' })
          }}
          disabled={isReturning}
          className="gap-3"
        >
          {isReturning ? <Spinner className="h-4 w-4" /> : <Undo2Icon className="h-4 w-4" />}
          {t`Go back`}
        </Item>
      )}
      <Item
        // base-ui uses `onClick` (not `onSelect`); closeOnClick={false} keeps the menu open so the
        // spinner stays visible until the redirect.
        closeOnClick={false}
        onClick={() => {
          if (isLoggingOut) return
          logout()
        }}
        disabled={isLoggingOut}
        className="text-muted-foreground gap-3"
      >
        {isLoggingOut ? <Spinner className="h-4 w-4" /> : <LogOutIcon className="h-4 w-4" />}
        {t`Close session`}
      </Item>
    </>
  )

  const dialogs = (
    <>
      {user?.type === 'applicant' && activeEmployerId === null && (
        <Suspense fallback={null}>
          <ProfileVisibilityDialog open={visibilityDialogOpen} onOpenChange={setVisibilityDialogOpen} />
        </Suspense>
      )}
      <Suspense fallback={null}>
        <ChangeLanguageDialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen} />
      </Suspense>
    </>
  )

  return (
    <>
      {isMobile ? (
        <Drawer open={visible} onOpenChange={setVisible}>
          <DrawerTrigger render={trigger} />
          <DrawerContent>
            {/* The identity header below is the sheet's visible heading; this names the surface for a screen
                reader without repeating it on screen. */}
            <DrawerTitle className="sr-only">{t`Account menu`}</DrawerTitle>
            {menuContent}
            {/* INSIDE the sheet: a dialog that stays open over it has to be a NESTED dialog, not a second
                top-level one — that's how base-ui knows to hand it the focus trap and to keep a press inside
                it from dismissing the sheet underneath. They portal out, so nothing lands in the panel's box.
                (Nothing needs them to outlive the sheet here: the rows that open them keep it open.) */}
            {dialogs}
          </DrawerContent>
        </Drawer>
      ) : (
        <>
          <DropdownMenu open={visible} onOpenChange={setVisible}>
            <DropdownMenuTrigger render={trigger} />
            <DropdownMenuContent className="w-64" side="bottom" align="end">
              {menuContent}
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Rendered OUTSIDE the menu so they survive the dropdown closing on select. */}
          {dialogs}
        </>
      )}
    </>
  )
}
